
import { defaultOf, comparePrimitives, createAtom } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { filter, tryFind, singleton, head, tail, isEmpty, truncate, cons, empty as empty_1, map, ofArray } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { fold, equalsWith, item, initialize, setItem, fill } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { split, join, concat, interpolate } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { FSharpSet__Add, FSharpSet__Contains, empty } from "../fable_modules/fable-library-js.5.13.0/Set.js";
import { Record } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { record_type, int32_type, string_type, float64_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { singleton as singleton_1, append, delay, toList } from "../fable_modules/fable-library-js.5.13.0/Seq.js";

export let enabled = createAtom(0);

const names = ofArray([["update", 1], ["view", 2], ["wire", 4], ["symbol", 8], ["sheet", 16], ["sim", 32], ["wave", 64], ["files", 128], ["misc", 256], ["perf", 512], ["mouse", 1024], ["all", 2047]]);

export const categoryNames = map((tuple) => tuple[0], names);

const ring = fill(new Array(400), 0, 400, "");

let ringNext = 0;

const problemRing = fill(new Array(100), 0, 100, "");

let problemNext = 0;

export let onErrorLogged = createAtom(undefined);

function emitAs(isProblem, tag, write, text) {
    const line = `[${interpolate("%7.3f%P()", [(typeof process !== 'undefined' && process.uptime ? process.uptime() : performance.now() / 1000)])}] ${tag} ${text}`;
    setItem(ring, ringNext % 400, line);
    ringNext = ((ringNext + 1) | 0);
    if (isProblem) {
        setItem(problemRing, problemNext % 100, line);
        problemNext = ((problemNext + 1) | 0);
    }
    write(line);
}

function emit(tag, write, text) {
    emitAs(false, tag, write, text);
}

/**
 * The result of something the user explicitly asked for from the Development menu - a benchmark,
 * a test run, a memory report. Always emitted: printing it is the point, and a category would
 * mean asking for the output twice.
 */
export function out(text) {
    emit("OUT", (s) => {
        console.log(s);
    }, text);
}

/**
 * **Something was asked for and did not happen.** Always emitted, kept in the problem ring for
 * the bug report, and in a debug build it puts the buffer on screen.
 * 
 * The line between this and `warn` is whether the outcome is still the right one, and NOT whose
 * fault it was. An OS command that failed and succeeded on retry is a `warn`: the file got
 * written, and there is nothing for anybody to do. The same command failing with no retry left
 * is an error even though the fault is the operating system's, because Issie did not do what it
 * was asked and a developer needs to know that - it is what decides whether Issie should retry,
 * fall back, or tell the user.
 * 
 * So a failure is a `warn` only when it was recovered from, or when Issie has given the user a
 * real answer instead: "this project will not load, here is why" is a defined outcome and a
 * perfectly good one. A failure that leaves a button doing nothing, a typed value not taking, or
 * a stale screen the user believes is live is an error whoever is to blame.
 * 
 * **Do not reach for `warn` to stop something repeating.** The renderer interrupts once per
 * distinct error, not once per occurrence - see `onErrorLogged`. Severity says what happened;
 * it is not a rate limit.
 * 
 * The line is written BEFORE `onError` runs, so that whatever the hook does - which in the
 * renderer is to open a popup showing the buffer - is looking at a buffer this error is already
 * in. A hook that throws is on its own: this is the error path, and an error while reporting an
 * error has nowhere left to go.
 */
export function error(text) {
    emitAs(true, "ERR", (s) => {
        console.error(s);
    }, text);
    if (onErrorLogged() == null) {
    }
    else {
        onErrorLogged()(text);
    }
}

/**
 * Something went wrong and the outcome is still right: a retry succeeded, a documented fallback
 * took over, or Issie gave the user a real answer about why it could not do what they asked.
 * Always emitted, and kept in the problem ring for the bug report, since a warning is often the
 * first sign of whatever is being reported. Unlike `error` it interrupts nobody, because there
 * is nothing for anybody to act on. See `error` for exactly where the line falls.
 */
export function warn(text) {
    emitAs(true, "WRN", (s) => {
        console.warn(s);
    }, text);
}

let warnedKeys = empty({
    Compare: (x, y) => (comparePrimitives(x, y) | 0),
});

/**
 * As warn, but only the first time for a given key. For a complaint on a path that can repeat
 * every frame of a drag, where an unconditional warn would itself be the bug.
 */
export function warnOnce(key, text) {
    if (!FSharpSet__Contains(warnedKeys, key)) {
        warnedKeys = FSharpSet__Add(warnedKeys, key);
        warn(text);
    }
}

/**
 * The ring buffer's contents, oldest first. This is what publish hands to a script, and what
 * lets a test assert that a run did - or did not - complain about something. A log line is
 * sometimes the only externally visible symptom of a bug, and one nothing can read is one no
 * test can hold on to.
 */
export function recentLines() {
    const array = initialize(400, (i) => item((ringNext + i) % 400, ring));
    return array.filter((line) => (line !== ""));
}

/**
 * Just the errors and warnings, oldest first. What the Bug Reports tab shows.
 */
export function recentProblems() {
    const array = initialize(100, (i) => item((problemNext + i) % 100, problemRing));
    return array.filter((line) => (line !== ""));
}

/**
 * One exception that got out of Issie's own code.
 * 
 * **There should never be any.** An exception Issie means to raise is raised at a place that
 * catches it, and the simulator is the worked example: it is full of `failwithf`s about states
 * that cannot arise, every one of them reachable from a design the user drew, and every one
 * caught by `Simulator.startCircuitSimulation` and turned into an `InternalError` the user is
 * asked to send us. Anything recorded here escaped instead, which means a bug, and the only
 * thing anybody can do about it afterwards is read it - so it is kept.
 */
export class UncaughtException extends Record {
    constructor(At, Source, Message, Stack, Repeats) {
        super();
        this.At = At;
        this.Source = Source;
        this.Message = Message;
        this.Stack = Stack;
        this.Repeats = (Repeats | 0);
    }
}

export function UncaughtException_$reflection() {
    return record_type("Log.UncaughtException", [], UncaughtException, () => [["At", float64_type], ["Source", string_type], ["Message", string_type], ["Stack", string_type], ["Repeats", int32_type]]);
}

let uncaught = empty_1();

/**
 * Record an exception that escaped. Called by the renderer's exception boundary and by nothing
 * else - the arguments are strings so that this stays free of any knowledge of what a thrown
 * JavaScript value looks like, which is the boundary's business.
 * 
 * A repeat of the exception already at the head bumps its count and is not logged again: the
 * case this exists for is one that fires every frame, where logging each one would itself be
 * the thing that made the app unusable.
 */
export function recordException(source, message, stack) {
    let last;
    let matchResult, last_1, rest_1;
    if (!isEmpty(uncaught)) {
        if ((tail(uncaught), (last = head(uncaught), (last.Source === source) && (last.Message === message)))) {
            matchResult = 0;
            last_1 = head(uncaught);
            rest_1 = tail(uncaught);
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0: {
            uncaught = cons(new UncaughtException((typeof process !== 'undefined' && process.uptime ? process.uptime() : performance.now() / 1000), last_1.Source, last_1.Message, last_1.Stack, last_1.Repeats + 1), rest_1);
            break;
        }
        case 1: {
            uncaught = cons(new UncaughtException((typeof process !== 'undefined' && process.uptime ? process.uptime() : performance.now() / 1000), source, message, stack, 1), truncate(10 - 1, uncaught));
            error(concat("uncaught exception in ", source, ": ", message));
            break;
        }
    }
}

/**
 * The exceptions that have escaped this session, newest first.
 */
export function recentExceptions() {
    return uncaught;
}

/**
 * Everything that has gone wrong this session, as the text of a bug report.
 * 
 * One blob rather than two, because it is written to be pasted somewhere by somebody who is
 * already annoyed: the exceptions first, since they are the part that should not exist at all,
 * and the errors and warnings after them as the run-up to whatever happened.
 */
export function problemReport() {
    let exceptionText;
    const matchValue = recentExceptions();
    exceptionText = (isEmpty(matchValue) ? singleton("(no uncaught exceptions - good)") : map((e) => {
        const repeats = (e.Repeats > 1) ? (` (x${e.Repeats})`) : "";
        const stack = (e.Stack === "") ? "  (no stack)" : e.Stack;
        return `[${interpolate("%7.3f%P()", [e.At])}] ${e.Source}${repeats}: ${e.Message}
${stack}`;
    }, matchValue));
    let problemText;
    const matchValue_1 = recentProblems();
    problemText = ((!equalsWith((x, y) => (x === y), matchValue_1, defaultOf()) && (matchValue_1.length === 0)) ? singleton("(nothing)") : ofArray(matchValue_1));
    return join("\n", toList(delay(() => append(singleton_1("=== uncaught exceptions, newest first ==="), delay(() => append(exceptionText, delay(() => append(singleton_1(""), delay(() => append(singleton_1("=== errors and warnings, oldest first ==="), delay(() => problemText)))))))))));
}

/**
 * A categorised debug line, discarded unless that category is on.
 * 
 * The message is an ordinary string, built by the caller: a thunk would read better but would
 * rely on Fable reducing away the lambda, and every site this is called from fires at most a few
 * times per user action, where building a template literal costs nothing. Guard with isOn on the
 * rare site that runs per frame.
 */
export function dbg(cat, text) {
    if ((enabled() & cat) !== 0) {
        emit("DBG", (s) => {
            console.log(s);
        }, text);
    }
}

/**
 * "wire,sim" -> the mask for those two. An unknown name is reported rather than silently
 * dropped: a typo that quietly logs nothing wastes more time than the log saves.
 */
export function maskOfNames(spec) {
    return fold((mask, name) => {
        const name_1 = name.trim().toLocaleLowerCase();
        const matchValue = tryFind((arg) => (name_1 === arg[0]), names);
        if (matchValue == null) {
            if (name_1 === "") {
                return mask | 0;
            }
            else {
                warn(concat("unknown log category \'", name_1, "\' - known categories are ", join(", ", categoryNames)));
                return mask | 0;
            }
        }
        else {
            return (mask | matchValue[1]) | 0;
        }
    }, 0, split(spec, [","], undefined, 0)) | 0;
}

/**
 * The names of the categories currently on, or "off". The inverse of maskOfNames, for a bug
 * report: what was being logged decides what the lines in it can be expected to say.
 */
export function namesOfMask(mask) {
    const matchValue = filter((tupledArg) => {
        if (tupledArg[0] !== "all") {
            return (mask & tupledArg[1]) !== 0;
        }
        else {
            return false;
        }
    }, names);
    if (isEmpty(matchValue)) {
        return "off";
    }
    else {
        return join(",", map((tuple) => tuple[0], matchValue));
    }
}

/**
 * Turn these categories on and all others off.
 */
export function setCategories(mask) {
    enabled(mask);
    warnedKeys = empty({
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    });
}

let msgCount = 0;

let renderCount = 0;

let updateMsTotal = 0;

let slowestMs = 0;

let slowestName = "";

let lastSummary = 0;

export let summaryInterval = createAtom(10);

export function countRender() {
    renderCount = ((renderCount + 1) | 0);
}

/**
 * Record one Elmish message and its duration. The name is only asked for when the message beat
 * the slowest so far, so the ordinary cost is an add and a compare.
 */
export function countMessage(ms, name) {
    msgCount = ((msgCount + 1) | 0);
    updateMsTotal = (updateMsTotal + ms);
    if (ms > slowestMs) {
        slowestMs = ms;
        slowestName = name();
    }
    if ((enabled() & 512) !== 0) {
        const now = (typeof process !== 'undefined' && process.uptime ? process.uptime() : performance.now() / 1000);
        if ((now - lastSummary) > summaryInterval()) {
            lastSummary = now;
            emit("PRF", (s) => {
                console.log(s);
            }, `${msgCount} msgs ${interpolate("%.0f%P()", [updateMsTotal])}ms, ${renderCount} renders, slowest ${slowestName} ${interpolate("%.1f%P()", [slowestMs])}ms`);
            msgCount = 0;
            renderCount = 0;
            updateMsTotal = 0;
            slowestMs = 0;
            slowestName = "";
        }
    }
}

/**
 * The log, and its switches, beside window.issie and window.issieKeys:
 * 
 * window.issieLog.lines()          the last few hundred lines, oldest first
 * window.issieLog.problems()       just the errors and warnings, oldest first
 * window.issieLog.report()         those and the uncaught exceptions, as bug report text
 * window.issieLog.on("wire,sim")   turn categories on, live
 * window.issieLog.off()
 * 
 * This is what lets scripts/inspect-canvas.js read a run's log without DevTools, and what lets
 * a category be turned on in a build somebody else is running. Published in every build for that
 * second reason: a debug-only hook is no use on the machine where the problem happens.
 */
export function publish() {
    window.issieLog = {
        lines: recentLines,
        off: () => {
            setCategories(0);
            return "logging off";
        },
        on: (spec) => {
            setCategories(maskOfNames(spec));
            return concat("logging: ", spec);
        },
        problems: recentProblems,
        report: problemReport,
    };
}

